import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Table,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Icon } from "../utils/iconHelper";
import StudyResourceService, { Test } from "../_services/StudyResourceService";
import "./TestBrowserModal.css";

interface TestBrowserModalProps {
  show: boolean;
  onHide: () => void;
  onSelectTest: (test: Test) => void;
  selectedTestId?: number; // Pre-select a test (for editing)
}

const TestBrowserModal: React.FC<TestBrowserModalProps> = ({
  show,
  onHide,
  onSelectTest,
  selectedTestId,
}) => {
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTests, setFilteredTests] = useState<Test[]>([]);

  useEffect(() => {
    if (show) {
      fetchTests();
    }
  }, [show]);

  useEffect(() => {
    // Filter tests based on search term
    if (searchTerm.trim() === "") {
      setFilteredTests(tests);
    } else {
      const filtered = tests.filter((test) =>
        test.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTests(filtered);
    }
  }, [searchTerm, tests]);

  const fetchTests = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedTests = await StudyResourceService.getAvailableTests();
      setTests(fetchedTests);
      setFilteredTests(fetchedTests);
    } catch (err: any) {
      setError(err.message || "Failed to fetch tests");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // Search is handled automatically via useEffect
  };

  const handleSelect = (test: Test) => {
    onSelectTest(test);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="test-browser-modal"
      enforceFocus={false}
      backdrop="static"
      onEscapeKeyDown={(e) => {
        e.stopPropagation();
        onHide();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <Icon.FileText className="me-2" />
          Select Test
        </Modal.Title>
      </Modal.Header>

      <Modal.Body onClick={(e) => e.stopPropagation()}>
        {/* Search */}
        <div className="mb-4">
          <InputGroup onClick={(e) => e.stopPropagation()} size="lg">
            <InputGroup.Text>
              <Icon.Search size={18} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search tests by name..."
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              onKeyPress={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") handleSearch();
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <Button
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSearch();
              }}
            >
              Search
            </Button>
          </InputGroup>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner role="status" animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading tests...</p>
          </div>
        ) : filteredTests.length === 0 ? (
          <div className="text-center py-5">
            <Icon.Inbox size={48} className="text-muted mb-3" />
            <h5 className="text-muted">No tests found</h5>
            <p className="text-muted">
              {searchTerm
                ? "Try adjusting your search"
                : "No tests available in the system"}
            </p>
          </div>
        ) : (
          <div className="test-browser-table">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Test Name</th>
                  <th style={{ width: "150px" }}>Category</th>
                  <th style={{ width: "150px" }}>Created</th>
                  <th style={{ width: "130px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTests.map((test) => (
                  <tr
                    key={test.id}
                    className={selectedTestId === test.id ? "table-active" : ""}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        <Icon.FileText className="me-2" size={16} />
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{test.name}</div>
                          <small className="text-muted">ID: {test.id}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {test.categoryName ? (
                        <Badge bg="secondary">{test.categoryName}</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-muted">{formatDate(test.createdDate)}</td>
                    <td>
                      <Button
                        variant={selectedTestId === test.id ? "success" : "primary"}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(test);
                        }}
                      >
                        {selectedTestId === test.id ? (
                          <>
                            <Icon.CheckCircle className="me-1" />
                            Selected
                          </>
                        ) : (
                          <>
                            <Icon.Plus className="me-1" />
                            Select
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            <strong>{filteredTests.length}</strong> test(s) available
            {searchTerm && ` (filtered from ${tests.length})`}
          </div>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TestBrowserModal;
